import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import isEqual from 'react-fast-compare';

import {
  FormErrors,
  LookupConfigModalContextInfo,
  LookupStepInfo,
  LookupStepTreeItemsById,
  LookupStepType,
} from 'lookup-config/LookupConfigModalConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';

/**
 * Applies the first-lookup-step interceptor logic used by SchemaFieldSelectTree.
 *
 * When the tree produces a new lookupStepGroup, we preserve the previous first lookup step info
 * (including any filterLookupSteps modifiers) if the first lookupStep itself is unchanged.
 * If it changed (e.g. a typecast was selected), we drop the modifiers since they are no longer valid.
 */
export function applyFirstLookupStepInterceptor(
  prevLookupStepGroup: Array<LookupStepInfo>,
  newLookupStepGroup: Array<LookupStepInfo>
): Array<LookupStepInfo> {
  if (prevLookupStepGroup.length === 0 || newLookupStepGroup.length === 0) {
    return newLookupStepGroup;
  }
  const prevFirstLookupStepInfo = prevLookupStepGroup[0];
  const newFirstLookupStepInfo = newLookupStepGroup[0];
  const didFirstLookupStepChange = !isEqual(
    prevFirstLookupStepInfo.lookupStep,
    newFirstLookupStepInfo.lookupStep
  );
  const firstLookupStepInfoToSet = didFirstLookupStepChange
    ? newFirstLookupStepInfo
    : prevFirstLookupStepInfo;
  return [firstLookupStepInfoToSet, ...newLookupStepGroup.slice(1)];
}

import {AddLookupStepButton} from '../AddLookupStepButton';
import {FilterLookupGroupPopover} from '../FilterLookupGroupPopover';
import {MAX_SCHEMA_FIELD_DEPTH} from '../LookupConfigModalConstants';
import SchemaFieldSelectTree from '../SchemaFieldSelectTree';
import DefaultFirstLookupStepForm from './DefaultFirstLookupStepForm';

export interface FirstLookupStepFormProps<TExtraState> {
  disabled: boolean;
  lookupStepGroup: Array<LookupStepInfo>;
  handleSetLookupStepGroup: (lookupStepGroup: Array<LookupStepInfo>) => void;
  extraState: TExtraState;
  setExtraState: (extraData: TExtraState) => void;
}

export interface FirstLookupStepFormSectionProps<TExtraState> {
  context?: LookupConfigModalContextInfo;
  renderFirstLookupStepForm?: (props: FirstLookupStepFormProps<TExtraState>) => JSX.Element;
  schemaFieldFilterFn?: (field: SchemaField, excludeNonLinkFields: boolean) => boolean;
  baseSchema: Schema | null;
  disabled: boolean;
  isCollapsed: boolean;
  setIsCollapsed: (isCollapsed: boolean) => void;
  firstLookupStepTypes: Array<LookupStepType>;
  shouldHideFilter?: boolean;
  intermediateLookupStepTypes: Array<LookupStepType>;
  lookupStepGroup: Array<LookupStepInfo>;
  // Caution: this callback is shared by SchemaFieldSelectTree. The SchemaFieldSelectTree itself is well-behaved,
  // and it will not blow anything away, but this component should be careful not to blow away lookupStepGroup[1]
  // unless the schema has actually changed and the SchemaFieldSelectTree actually needs to reset.
  handleSetLookupStepGroup: (lookupStepGroup: Array<LookupStepInfo>) => void;
  handleAddLookupStepGroup: (lookupStepGroup: Array<LookupStepInfo>) => void;
  formErrors: FormErrors;
  setFormErrors: (formErrors: FormErrors) => void;
  basePaneInvalidLookupObjectErrorMessage?: () => JSX.Element;
  useSchemaLanguage?: boolean;
  extraState: TExtraState;
  setExtraState: (extraData: TExtraState) => void;
  shouldAlwaysShowFirstSelectedItemInTree?: boolean;
  schemaFieldTreeFilterFn?: (field: SchemaField) => boolean;
  allowAlternatingGridPatterns?: boolean;
  selectTreeItemsById: LookupStepTreeItemsById;
  setSelectTreeItemsById: (items: LookupStepTreeItemsById) => void;
  typecastSchemasByNodeId: Map<string, Schema | null>;
  updateTypecastSchemasByNodeId: (nodeId: string, typecastSchema: Schema | null) => void;
}

/**
 * The first section of the first pane of the LookupConfigModal.
 *
 * This first section is special, because it needs to be specialized for whatever "lookup environment"
 * the LookupConfigModal is being used in, whether that environment is Lab Automation, Notebook, or something else.
 *
 * This component is made up of two parts:
 * - A "form" which is specialized for the lookup environment. This "form" can look very different depending on the lookup environment.
 * - A "post-form region" which includes a SchemaFieldSelectTree. This "post-form region" should look the same in every environment.
 */
export default function FirstLookupStepFormSection<TExtraState>(
  props: FirstLookupStepFormSectionProps<TExtraState>
): JSX.Element {
  let firstLookupStepForm: JSX.Element;
  if (props.renderFirstLookupStepForm) {
    firstLookupStepForm = props.renderFirstLookupStepForm({
      disabled: props.disabled,
      lookupStepGroup: props.lookupStepGroup,
      handleSetLookupStepGroup: props.handleSetLookupStepGroup,
      extraState: props.extraState,
      setExtraState: props.setExtraState,
    });
  } else {
    firstLookupStepForm = (
      <DefaultFirstLookupStepForm
        disabled={props.disabled}
        context={props.context}
        baseSchema={props.baseSchema}
        isCollapsed={props.isCollapsed}
        setIsCollapsed={props.setIsCollapsed}
        firstLookupStepTypes={props.firstLookupStepTypes}
        lookupStepGroup={props.lookupStepGroup}
        handleSetLookupStepGroup={props.handleSetLookupStepGroup}
        handleAddLookupStepGroup={props.handleAddLookupStepGroup}
        formErrors={props.formErrors}
        basePaneInvalidLookupObjectErrorMessage={props.basePaneInvalidLookupObjectErrorMessage}
        schemaFieldFilterFn={props.schemaFieldFilterFn}
        allowAlternatingGridPatterns={props.allowAlternatingGridPatterns}
      />
    );
  }

  return (
    <>
      {firstLookupStepForm}
      <SchemaFieldSelectTree
        label={props.useSchemaLanguage ? 'Schema' : undefined}
        disabled={props.disabled}
        lookupStepGroup={props.lookupStepGroup}
        handleSetLookupStepGroup={(lookupStepGroup) => {
          props.handleSetLookupStepGroup(
            applyFirstLookupStepInterceptor(props.lookupStepGroup, lookupStepGroup)
          );
        }}
        depth={MAX_SCHEMA_FIELD_DEPTH}
        shouldAlwaysShowSelectedItem={props.shouldAlwaysShowFirstSelectedItemInTree}
        schemaFieldTreeFilterFn={props.schemaFieldTreeFilterFn}
        selectTreeItemsById={props.selectTreeItemsById}
        setSelectTreeItemsById={props.setSelectTreeItemsById}
        typecastSchemasByNodeId={props.typecastSchemasByNodeId}
        updateTypecastSchemasByNodeId={props.updateTypecastSchemasByNodeId}
      />
      {!props.shouldHideFilter && (
        <FilterLookupGroupPopover
          disabled={props.disabled}
          lookupStepGroup={props.lookupStepGroup}
          handleSetLookupStepGroup={props.handleSetLookupStepGroup}
          schemaFieldFilterFn={props.schemaFieldFilterFn}
        />
      )}
      {!props.disabled && props.lookupStepGroup.length && props.intermediateLookupStepTypes.length ? (
        <div className={css(styles.FirstAddLookupStepButton)}>
          <AddLookupStepButton
            previousStepGroup={props.lookupStepGroup}
            setFormErrors={props.setFormErrors}
            handleSetLookupStep={(lookupStepInfo: LookupStepInfo) => {
              props.handleAddLookupStepGroup([lookupStepInfo]);
            }}
            context={props.context}
            allowedLookupStepTypes={props.intermediateLookupStepTypes}
            baseSchema={props.baseSchema}
          />
        </div>
      ) : null}
    </>
  );
}

const styles = StyleSheet.create({
  FirstAddLookupStepButton: {
    marginBottom: '-10px',
  },
});
